import { GetPartnerBusinessProfileResponse } from '@/api/type/types';
import styled from 'styled-components';
import ProfileInput from '../component/ProfileInput';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { businessProfile } from '@/stores/slices/business-profile/businessProfileSlice';
import { formatInternationalNumber } from '@/utils/number/formatInternationalNumber';
import { useTranslation } from 'react-i18next';
import BusinessAddress from '../profile-info/middle/BusinessAddress';
import { MultiLineText } from '@/components/text/MultiLineText';
import { useAppSelector } from '@/stores/hooks';
import { LOCAL_STORAGE_KEYS } from '@/constants/storage';
import ProfileTitle from '../component/ProfileTitle';
import { ROLE_TYPE } from '@/api/type/commonTypes';

interface CenterInfoProps {
  profile: GetPartnerBusinessProfileResponse;
  isEdit: boolean;
}
const CenterInfo = ({ profile, isEdit }: CenterInfoProps) => {
  const putProfile = useAppSelector(
    (state) => state.businessProfile.putBusinessProfile
  );
  const partnerNameValue =
    isEdit && putProfile?.partnerName !== undefined
      ? putProfile.partnerName
      : profile.partnerName;

  const contactValue =
    isEdit && putProfile?.partnerContactNumber !== undefined
      ? putProfile.partnerContactNumber
      : profile.partnerContactNumber;

  const headNameValue =
    isEdit && putProfile?.partnerHeadName !== undefined
      ? putProfile.partnerHeadName
      : profile.partnerHeadName;
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const onChangePartnerName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        businessProfile.putBusinessProfile({
          partnerName: e.target.value,
        })
      );
    },
    [dispatch]
  );

  const onChangeContactNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const onlyNumbers = e.target.value.replace(/\D/g, '');

      dispatch(
        businessProfile.putBusinessProfile({
          partnerContactNumber: onlyNumbers,
        })
      );
    },
    [dispatch]
  );

  const onChangeHeadName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        businessProfile.putBusinessProfile({
          partnerHeadName: e.target.value,
        })
      );
    },
    [dispatch]
  );

  const role = localStorage.getItem(LOCAL_STORAGE_KEYS.ROLE_TYPE) as ROLE_TYPE;
  return (
    <Container data-en={i18n.language === 'en'}>
      <Section>
        <Row>
          <ProfileTitle
            title={
              role === 'CENTER' || 'MGG_CENTER' ? t('r2Center') : t('r2Planner')
            }
            star
            alignCenter="center"
          />
          <ProfileInput
            isEdit={isEdit}
            value={partnerNameValue}
            onChange={onChangePartnerName}
            inputId="r2CenterPlanner"
            inputName="r2CenterPlanner"
            inputAriaLabel="r2Center Planner"
            width="419px"
          />
        </Row>
        <Row>
          <ProfileTitle title={t('officeNo')} star alignCenter="center" />
          {!isEdit && (
            <DefaultWrap>
              <ProfileInput
                isEdit={false}
                value={formatInternationalNumber({
                  countryCode: profile.name.countryCode,
                  number: contactValue,
                })}
                onChange={onChangeContactNumber}
                type="tel"
                inputId="officeNo"
                inputName="officeNo"
                inputAriaLabel="office No"
              />
            </DefaultWrap>
          )}
          {isEdit && (
            <DefaultWrap>
              <DefaultData>{profile.name.countryCode}</DefaultData>
              <ProfileInput
                isEdit={true}
                value={contactValue}
                onChange={onChangeContactNumber}
                type="tel"
                inputId="countryCode"
                inputName="countryCode"
                inputAriaLabel="country Code"
                width="354px"
              />
            </DefaultWrap>
          )}
        </Row>
        <Row>
          <ProfileTitle title={t('representative')} star alignCenter="center" />
          <ProfileInput
            isEdit={isEdit}
            value={headNameValue}
            onChange={onChangeHeadName}
            inputId="representative"
            inputName="representative"
            inputAriaLabel="representative name"
            width="419px"
          />
        </Row>
        <Row>
          <ProfileTitle title={t('businessAddress')} star />
          <Value>
            <BusinessAddress isEdit={isEdit} profile={profile} />
          </Value>
        </Row>
      </Section>
    </Container>
  );
};

export default CenterInfo;

const DefaultWrap = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
`;

const DefaultData = styled.div`
  font-size: 15px;
  line-height: 140%;
  color: #151414;
  width: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: 1px solid #b974ff;
  border-radius: 9px;
`;

const Section = styled.div`
  padding: 26px 43px;
  display: flex;
  flex-direction: column;
  text-align: start;
  gap: 32px;
`;

const Row = styled.div`
  display: flex;
  gap: 18px;
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 20px;
  line-height: 140%;
  width: 147px;
  color: #6e98f8;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  border-bottom: 1px dotted #6a96f8;
  &[data-en='true'] {
    ${Row} {
      gap: 0;
    }
    ${Label} {
      width: 165px;
    }
  }
`;

const Value = styled.div`
  font-size: 15px;
  line-height: 140%;
  color: #151131;
  display: flex;
  flex: 1;
`;

const LabelRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const InlineTag = styled.span`
  text-align: end;
  font-size: 15px;
`;
